import React, { useState, useContext, useEffect, useMemo, useCallback, useRef, Suspense, lazy } from "react";
import clsx from "clsx";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
  Avatar,
  Badge,
  withStyles,
  Chip,
  BottomNavigation,
  BottomNavigationAction,
  Hidden,
  Box,
} from "@material-ui/core";

import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";

import MenuIcon from "@material-ui/icons/Menu";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ForumIcon from "@material-ui/icons/Forum";
import ChatIcon from "@material-ui/icons/Chat";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import CachedIcon from "@material-ui/icons/Cached";
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom";
import AccessTimeIcon from "@material-ui/icons/AccessTime";

import MainListItems from "./MainListItems";
import { detectDeviceType } from "../helpers/detectDeviceType";
import NotificationsPopOver from "../components/NotificationsPopOver";
import RemindersPopover from "../components/RemindersPopover";
import NotificationsVolume from "../components/NotificationsVolume";
import Tooltip from "@material-ui/core/Tooltip";
import { AuthContext } from "../context/Auth/AuthContext";
import { NotificationsContext } from "../context/Notifications/NotificationsContext";
import BackdropLoading from "../components/BackdropLoading";
import api from "../services/api";
import { TicketsContext } from "../context/Tickets/TicketsContext";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import ChatPopover from "../pages/Chat/ChatPopover";

import { useDate } from "../hooks/useDate";
import UserLanguageSelector from "../components/UserLanguageSelector";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";

import ColorModeContext from "./themeContext";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import { getBackendUrl } from "../config";
import useSettings from "../hooks/useSettings";
import useCompanySettings from "../hooks/useSettings/companySettings";
import usePWA from "../hooks/usePWA";
import VersionControl from "../components/VersionControl";
import PWAModal from "../components/PWAModal";
import AIAssistant from "../components/AIAssistant";
import SystemNoticeGate from "../components/SystemNoticeGate";

// logos (fallbacks)
import logo from "../assets/logo-black.png";
import logoDark from "../assets/logo.png";
// Lazy: o modal de perfil so abre sob clique, mas como import estatico
// arrastava formik + yup (~51 KB) para o main.js que toda sessao baixa.
const UserModal = lazy(() => import("../components/UserModal"));

const backendUrl = getBackendUrl();
// Largura do sidebar expandido: ajustada com base no maior item ("Configurações Globais"),
// eliminando o espaço vazio excessivo à direita e aproximando o menu do texto.
const DRAWER_WIDTH_DEFAULT = 202;
const DRAWER_WIDTH_MIN = 192;
const DRAWER_WIDTH_MAX = 212;
// Margem extra após o fim do maior rótulo (respiro visual suave)
const DRAWER_WIDTH_EXTRA_MARGIN = 10;
const collapsedDrawerWidth = 68;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    // Mobile Safari: 100vh inclui a barra de endereço — usar dvh como override
    "@supports (height: 100dvh)": {
      height: "100dvh",
    },
    [theme.breakpoints.down("sm")]: {
      // --app-height é setada via visualViewport API (index.js) e encolhe
      // quando o teclado virtual abre no iOS/Android
      height: "var(--app-height, 100dvh)",
      maxHeight: "var(--app-height, 100dvh)",
      overflow: "hidden",
    },
    backgroundColor: theme.palette.fancyBackground,
    "& .MuiButton-outlinedPrimary": {
      color: theme.palette.primary,
      border:
        theme.mode === "light"
          ? "1px solid rgba(0 124 102)"
          : "1px solid rgba(255, 255, 255, 0.5)",
    },
    "& .MuiTab-textColorPrimary.Mui-selected": {
      color: theme.palette.primary,
    },
  },

  chip: { background: "red", color: "white" },
  avatar: { width: "100%" },

  toolbar: {
    paddingRight: 24,
    color: "#FFFFFF",
    background: theme.palette.barraSuperior,
    gap: theme.spacing(1),
    overflow: "visible",
    borderBottom: `1px solid ${theme.palette.dividerColor || "rgba(0,0,0,0.06)"}`,
    boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
    [theme.breakpoints.down("sm")]: {
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1),
      minHeight: 48,
      gap: theme.spacing(0.5),
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
    },
  },

  // SCROLLER HORIZONTAL (ícones)
  topbarScroller: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flex: "1 1 0%",
    minWidth: 0,
    maxWidth: "100%",
    flexWrap: "nowrap",

    // DESKTOP: alinhar à direita
    justifyContent: "flex-end",
    overflowX: "visible",

    // cada filho não encolhe => gera overflow quando somar mais que a largura
    "& > *": { flex: "0 0 auto" },

    // MOBILE: alinhar à esquerda + scroll horizontal invisível
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-end",
      overflowX: "auto",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch",
      touchAction: "pan-x",
      overscrollBehaviorX: "contain",
      msOverflowStyle: "none",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
  },

  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 14px",
    minHeight: "56px",
    [theme.breakpoints.down("sm")]: { minHeight: "48px" },
  },

  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    [theme.breakpoints.down("sm")]: {
      zIndex: 1301, // acima do BottomNavigation (1300) e do Drawer
    },
  },

  menuButtonHidden: { display: "none" },

  title: {
    flexGrow: 0,
    fontSize: 13,
    fontWeight: 500,
    color: "rgba(255,255,255,0.85)",
    marginLeft: theme.spacing(1),
    [theme.breakpoints.down("sm")]: { display: "none" },
  },

  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: (props) => (props.drawerOpen ? (props.drawerWidth || DRAWER_WIDTH_DEFAULT) : collapsedDrawerWidth),
    height: "100vh",
    maxHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
    overflowY: "hidden",
    background: theme.palette.sidebarBackground,
    color: "#FFFFFF",
    borderRight: "none",
    boxShadow: "4px 0 24px rgba(0,0,0,0.18)",
  },
  drawerPaperClose: {
    overflowX: "hidden",
    overflowY: "hidden",
    height: "100vh",
    maxHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: `${collapsedDrawerWidth}px !important`,
    maxWidth: `${collapsedDrawerWidth}px !important`,
    [theme.breakpoints.up("sm")]: {
      width: `${collapsedDrawerWidth}px !important`,
      maxWidth: `${collapsedDrawerWidth}px !important`,
    },
    background: theme.palette.sidebarBackground,
    boxShadow: "2px 0 12px rgba(0,0,0,0.14)",
  },

  appBarSpacer: {
    minHeight: 56,
    [theme.breakpoints.down("sm")]: {
      minHeight: 48,
    },
  },

  content: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    height: "100%", // Preenche o espaço restante no root (que é 100vh)
    overflow: "hidden",
    position: "relative",
    backgroundColor: theme.palette.fancyBackground,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    width: "100%",
    paddingBottom: 0,
    overflowY: "auto", // Permite scroll se o conteúdo for maior que a tela
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: 64, // Altura do BottomNavigation
    },
  },

  contentWrapperNoBottom: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    width: "100%",
    paddingBottom: 0,
    overflowY: "auto",
    overflowX: "hidden",
  },

  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },

  containerWithScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    padding: 0,
    margin: 0,
    borderRadius: 0,
    border: "none",
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(255, 255, 255, 0.25) transparent",
    "&::-webkit-scrollbar": {
      width: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(255, 255, 255, 0.25)",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "rgba(255, 255, 255, 0.45)",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
  },

  logoImg: {
    height: 38,
    width: "auto",
    maxWidth: "160px",
    objectFit: "contain",
    transition: "all 0.3s ease",
  },
  hideLogo: { display: "none" },

  avatar2: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    cursor: "pointer",
    borderRadius: "50%",
    border: "2px solid rgba(255,255,255,0.5)",
    transition: "border-color 200ms ease, transform 200ms ease",
    "&:hover": {
      borderColor: "#fff",
      transform: "scale(1.08)",
    },
  },

  compressIconButton: {
    [theme.breakpoints.down("sm")]: { padding: 6 },
  },

  bottomNav: {
    width: "100%",
    position: "fixed",
    bottom: 0,
    zIndex: theme.zIndex.drawer + 1,
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.barraSuperior,
    height: 56,
  },

  bottomNavAction: {
    color: "rgba(255, 255, 255, 0.7)",
    "&.Mui-selected": {
      color: "#fff",
      "& .MuiBottomNavigationAction-label": {
        fontSize: "0.75rem",
        fontWeight: 700,
      }
    },
    minWidth: "auto",
    padding: "6px 0",
  },
}));

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": { transform: "scale(.8)", opacity: 1 },
    "100%": { transform: "scale(2.4)", opacity: 0 },

  },
}))(Badge);

// Relogio do cabecalho. Usava moment so para isto, e como o layout monta em
// toda tela, moment (61 KB) + o locale pt-br entravam no main.js. Date + Intl
// fazem o mesmo nativamente, sem nada no bundle.
const pad2 = (n) => String(n).padStart(2, "0");

const Clock = React.memo(({ timezone }) => {
  const [time, setTime] = useState(() => new Date());
  const activeTimezone = timezone || "America/Cuiaba";

  useEffect(() => {
    // Atualizar a cada 30s em vez de 1s — reduz re-renders em 30x no mobile
    const timer = setInterval(() => {
      setTime(new Date());
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const timeString = useMemo(() => {
    try {
      const tz = activeTimezone;

      // Fuso fixo (Etc/GMT): no padrao Etc/GMT, +4 significa UTC-4.
      if (tz.includes("Etc/GMT")) {
        const offsetPart = tz.replace("Etc/GMT", "");
        const offset = parseInt(offsetPart, 10) * -1;
        const shifted = new Date(time.getTime() + offset * 3600 * 1000);
        return `${pad2(shifted.getUTCHours())}:${pad2(shifted.getUTCMinutes())}`;
      }

      return new Intl.DateTimeFormat("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: tz,
        hour12: false
      }).format(time);
    } catch (e) {
      return `${pad2(time.getHours())}:${pad2(time.getMinutes())}`;
    }
  }, [time, activeTimezone]);

  return timeString;
});

const LoggedInLayout = ({ children }) => {
  const [autoDrawerWidth, setAutoDrawerWidth] = useState(DRAWER_WIDTH_DEFAULT);
  const sidebarListRef = useRef(null);
  const [drawerPinned, setDrawerPinned] = useState(() => {
    return localStorage.getItem("drawerPinned") === "true";
  });
  const [drawerOpen, setDrawerOpen] = useState(() => {
    return localStorage.getItem("drawerPinned") === "true";
  });
  const classes = useStyles({ drawerWidth: autoDrawerWidth, drawerOpen });
  const [userToken, setUserToken] = useState("disabled");
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, handleLogout, loading, socket, timezone, setTimezone } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const contentWrapperRef = useRef(null);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const [bottomNavValue, setBottomNavValue] = useState(0);
  const { notifications } = useContext(NotificationsContext);
  const isMountedRef = useRef(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTicketsPage = location.pathname.startsWith("/tickets");
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));
  const { isInstallable, isInstalled, installApp } = usePWA();
  const [pwaModalOpen, setPwaModalOpen] = useState(false);

  useEffect(() => {
    const path = location.pathname;
    if (path === "/tickets") setBottomNavValue(0);
    else if (path.startsWith("/kanban")) setBottomNavValue(1);
    else if (path.startsWith("/contacts")) setBottomNavValue(2);
    else if (path.startsWith("/chats")) setBottomNavValue(3);
    else setBottomNavValue(4);

    if (contentWrapperRef.current) {
      contentWrapperRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleCustomEvent = () => {
      const isPinned = localStorage.getItem("drawerPinned") === "true";
      setDrawerPinned(isPinned);
      setDrawerOpen(isPinned);
    };
    window.addEventListener("drawerPinnedChanged", handleCustomEvent);
    return () => {
      window.removeEventListener("drawerPinnedChanged", handleCustomEvent);
    };
  }, []);

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);
  const { dateToClient } = useDate();
  const [profileUrl, setProfileUrl] = useState(null);
  const [supportWhatsapp, setSupportWhatsapp] = useState("");
  const [isDrawerHovered, setIsDrawerHovered] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const drawerPaperRef = useRef(null);

  const toggleDrawer = useCallback(() => {
    setDrawerPinned((prev) => {
      const next = !prev;
      setDrawerOpen(next);
      localStorage.setItem("drawerPinned", String(next));
      return next;
    });
  }, []);

  const handleDrawerMouseEnter = useCallback(() => {
    if (drawerPinned) return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (greaterThenSm) {
      setIsDrawerHovered(true);
      setDrawerOpen(true);
    }
  }, [greaterThenSm, drawerPinned]);

  const handleDrawerMouseMove = useCallback(() => {
    if (drawerPinned) return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (greaterThenSm) {
      setIsDrawerHovered(true);
      setDrawerOpen(true);
    }
  }, [greaterThenSm, drawerPinned]);

  const handleDrawerMouseLeave = useCallback((e) => {
    if (drawerPinned) return;
    if (greaterThenSm) {
      const currentWidth = autoDrawerWidth || DRAWER_WIDTH_DEFAULT;
      if (typeof e?.clientX === "number" && e.clientX >= 0 && e.clientX <= currentWidth) {
        return;
      }

      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => {
        if (!drawerPinned) {
          setIsDrawerHovered(false);
          setDrawerOpen(false);
        }
      }, 300);
    }
  }, [greaterThenSm, drawerPinned, autoDrawerWidth]);

  const handleMenuInteract = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (greaterThenSm) {
      setIsDrawerHovered(true);
      setDrawerOpen(true);
    }
  }, [greaterThenSm]);

  // Mede a largura real do maior item do menu (ícone + texto + setinha se houver)
  // e ajusta a largura do sidebar expandido para caber nele + uma margem suave,
  // dentro dos limites mínimo/máximo definidos acima.
  const measureSidebarWidth = useCallback(() => {
    if (!sidebarListRef.current) return;
    const items = sidebarListRef.current.querySelectorAll(".MuiListItem-root");
    let maxWidth = 0;
    items.forEach((item) => {
      const textEl = item.querySelector(".MuiListItemText-root");
      const expandIcon = item.querySelector("svg[class*='expandIcon']");
      const itemRect = item.getBoundingClientRect();

      let rightEdge = 0;
      if (textEl) {
        const textSpan = textEl.querySelector("span, p, *") || textEl;
        const textWidth = textSpan.scrollWidth || textSpan.getBoundingClientRect().width;
        const textLeft = textEl.getBoundingClientRect().left;
        rightEdge = textLeft + textWidth;
      }
      if (expandIcon) {
        rightEdge = Math.max(rightEdge, expandIcon.getBoundingClientRect().right + 2);
      }
      if (rightEdge > 0 && itemRect.left >= 0) {
        const itemWidth = rightEdge - itemRect.left;
        if (itemWidth > maxWidth) maxWidth = itemWidth;
      }
    });
    if (maxWidth > 0) {
      const target = Math.min(
        Math.max(Math.round(maxWidth + DRAWER_WIDTH_EXTRA_MARGIN), DRAWER_WIDTH_MIN),
        DRAWER_WIDTH_MAX
      );
      setAutoDrawerWidth((prev) => (Math.abs(prev - target) > 2 ? target : prev));
    }
  }, []);

  useEffect(() => {
    if (!drawerOpen || isMobile) return;
    const raf = requestAnimationFrame(measureSidebarWidth);
    return () => cancelAnimationFrame(raf);
  }, [drawerOpen, isMobile, measureSidebarWidth]);

  const handleTogglePin = useCallback(() => {
    toggleDrawer();
  }, [toggleDrawer]);

  const drawerClose = useCallback(() => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    // Corrigido 08/10/2026: este efeito reabria o Drawer (sidebar) toda vez
    // que a rota mudava, sem checar se estava no mobile. No mobile o Drawer
    // é "temporary" (modal), entao sempre que drawerPinned estava true no
    // localStorage (herdado do desktop), qualquer navegacao pelo
    // BottomNavigation reabria o menu lateral por cima da tela.
    if (isMobile) return;
    if (drawerPinned) {
      setDrawerOpen(true);
    } else if (greaterThenSm && isDrawerHovered) {
      setDrawerOpen(true);
    }
  }, [greaterThenSm, location.pathname, isDrawerHovered, drawerPinned, isMobile]);

  const mainListItems = useMemo(
    () => (
      <MainListItems
        drawerOpen={drawerOpen}
        collapsed={!drawerOpen}
        drawerClose={drawerClose}
        onMenuInteract={handleMenuInteract}
      />
    ),
    [drawerOpen, drawerClose, handleMenuInteract]
  );

  const settings = useSettings();
  const { get: getSetting } = settings;
  const companySettings = useCompanySettings();

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isInstallable || isInstalled) {
      setPwaModalOpen(false);
      return undefined;
    }

    if (sessionStorage.getItem("pwa_modal_seen")) {
      return undefined;
    }

    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setPwaModalOpen(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [isInstallable, isInstalled]);

  // Buscar WhatsApp de suporte (Whitelabel → fallback .env)
  useEffect(() => {
    let isActive = true;

    const fetchSupportWhatsapp = async () => {
      try {
        const { data } = await api.get("/global-config/public-branding");
        const fromPanel = data?.loginWhatsapp;
        const fromEnv = process.env.REACT_APP_NUMBER_SUPPORT
          ? `https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`
          : "";
        if (isActive && isMountedRef.current) {
          setSupportWhatsapp(fromPanel || fromEnv);
        }
      } catch (err) {
        console.error("Erro ao carregar número de suporte:", err);
        if (isActive && isMountedRef.current && process.env.REACT_APP_NUMBER_SUPPORT) {
          setSupportWhatsapp(`https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`);
        }
      }
    };
    fetchSupportWhatsapp();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    document.body.classList.remove("login-page");
    let isActive = true;

    const loadUserTokenSetting = async () => {
      try {
        await getSetting("wtV");
        if (isActive && isMountedRef.current) {
          setUserToken("disabled");
        }
      } catch (err) {
        if (isActive) {
          console.log("Error loading setting wtV in layout", err);
        }
      }
    };
    loadUserTokenSetting();

    return () => {
      isActive = false;
    };
  }, [getSetting]);

  useEffect(() => {
    // Timezone agora é gerenciado pelo AuthContext/useAuth
  }, [user.company_id]);

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      if (user.defaultMenu === "open") {
        setDrawerPinned(true);
        setDrawerOpen(true);
        localStorage.setItem("drawerPinned", "true");
      } else if (user.defaultMenu === "closed") {
        setDrawerPinned(false);
        setDrawerOpen(false);
        localStorage.setItem("drawerPinned", "false");
      } else {
        const isPinned = localStorage.getItem("drawerPinned") === "true";
        setDrawerPinned(isPinned);
        setDrawerOpen(isPinned);
      }
    }
    if (user.defaultTheme === "dark" && theme.mode === "light") {
      colorMode.toggleColorMode();
    }
  }, [user.defaultMenu, user.defaultTheme]);

  useEffect(() => {
    if (isMobile) setDrawerVariant("temporary");
    else setDrawerVariant("permanent");
  }, [isMobile]);

  useEffect(() => {
    const company_id = user.company_id;
    const user_id = user.id;
    if (company_id) {
      const ImageUrl = user.profileImage;
      if (ImageUrl !== undefined && ImageUrl !== null)
        isMountedRef.current && setProfileUrl(`${backendUrl}/public/avatar/${ImageUrl}`);
      else if (isMountedRef.current) setProfileUrl(`${process.env.FRONTEND_URL}/nopicture.png`);

      const onCompanyAuthLayout = (data) => {
        // Corrigido 08/10/2026: antes disso TODO login do mesmo usuário
        // derrubava qualquer outra aba/dispositivo já aberto, mesmo com
        // "Forçar Logout" desabilitado na empresa — o backend já filtra isso
        // (só emite o evento quando a config está habilitada), e agora só
        // derruba a sessão se for do MESMO tipo de dispositivo, permitindo
        // 1 sessão desktop + 1 sessão mobile simultâneas.
        if (data.user.id === +user_id && data.deviceType === detectDeviceType()) {
          toastError("Sua conta foi acessada em outro dispositivo do mesmo tipo (desktop/mobile).");
          setTimeout(() => {
            localStorage.clear();
            window.location.reload();
          }, 1000);
        }
      };

      if (socket && typeof socket.on === "function") {
        socket.on(`company-${company_id}-auth`, onCompanyAuthLayout);
        // Settings agora são ouvidos no useAuth

        socket.emit("userStatus");

        
        // isolamento de dados no socket
        if (user.whatsapp_id) {
          socket.emit("joinWhatsapp", user.whatsapp_id);
        }
        if (user.profile === "admin" || user.profile === "super_admin" || user.super) {
          socket.emit("joinAdmins"); 
        }
        
        socket.emit("joinUser", user.id);
        socket.emit("joinNotification");
      }

      const interval = setInterval(() => {
        if (socket && typeof socket.emit === "function") {
          socket.emit("userStatus");
        }
      }, 1000 * 60 * 5);

      return () => {
        if (socket && typeof socket.off === "function") {
          socket.off(`company-${company_id}-auth`, onCompanyAuthLayout);
        }
        clearInterval(interval);
      };
    }
  }, [socket, user.company_id, user.id, user.profile, user.profileImage, user.super, user.whatsapp_id]);



  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };
  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };
  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };
  const handleRefreshPage = () => window.location.reload(false);

  const handleBackToSupport = async () => {
    const supportToken = localStorage.getItem("supportToken");
    const supportCompanyId = localStorage.getItem("supportCompanyId");
    const supportUserId = localStorage.getItem("supportUserId");

    if (supportToken) {
      try {
        const { data } = await api.post("/companies/unimpersonate", { supportUserId });

        localStorage.clear();

        if (data && data.token) {
          localStorage.setItem("token", JSON.stringify(data.token));
          if (data.user && data.user.company_id) {
            localStorage.setItem("company_id", data.user.company_id);
            localStorage.setItem("user_id", data.user.id);
            localStorage.setItem("user", JSON.stringify(data.user));
          }
        }
        
        window.location.href = "/";
      } catch (err) {
        console.error("Erro ao desfazer simulação no backend:", err);
        // Fallback: se falhar, restaura o token antigo e limpa a simulação
        localStorage.clear();
        localStorage.setItem("token", supportToken);
        if (supportCompanyId && supportCompanyId !== "null" && supportCompanyId !== "undefined") {
          localStorage.setItem("company_id", supportCompanyId);
        }
        if (supportUserId && supportUserId !== "null" && supportUserId !== "undefined") {
          localStorage.setItem("user_id", supportUserId);
        }
        window.location.href = "/";
      }
    }
  };

  const handleBackToMaster = () => {
    const originalToken = localStorage.getItem("originalToken");
    const originalCompanyId = localStorage.getItem("originalCompanyId");
    const originalUserId = localStorage.getItem("originalUserId");

    if (originalToken) {
      localStorage.clear(); // Limpa as configurações e dados da empresa simulada

      localStorage.setItem("token", originalToken);
      localStorage.setItem("company_id", originalCompanyId);
      localStorage.setItem("user_id", originalUserId);

      window.location.href = "/companies-master";
    }
  };

  const handleOpenSupport = useCallback(async () => {
    handleCloseMenu();
    try {
      const { data: ticket } = await api.post("/support-tickets");
      history.push(`/tickets/${ticket.uuid}`);
    } catch (err) {
      toastError(err);
    }
  }, [history]);

  const handleClosePwaModal = () => {
    setPwaModalOpen(false);
    sessionStorage.setItem("pwa_modal_seen", "true");
  };

  const handleInstallApp = async () => {
    if (isInstallable && !isInstalled) {
      await installApp();
      handleClosePwaModal();
    } else {
      toast.info("Para instalar: No Chrome/Edge, clique nos 3 pontos (superior direito) > 'Instalar Aplicativo'. No iPhone: Botão Compartilhar > 'Adicionar à Tela de Início'.");
    }
    handleCloseMenu();
  };

  const handleInstallFromModal = async () => {
    await installApp();
    handleClosePwaModal();
  };

  if (loading) return <BackdropLoading />;

  // src da logo para o AppBar (sempre Dark devido ao fundo escuro da barra)
  const logoAppBar = typeof theme.calculatedLogoDark === "function" 
    ? theme.calculatedLogoDark() 
    : logoDark;

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: drawerOpen ? classes.drawerPaper : classes.drawerPaperClose,
        }}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onMouseEnter={handleDrawerMouseEnter}
        onMouseMove={handleDrawerMouseMove}
        onMouseLeave={handleDrawerMouseLeave}
        PaperProps={{
          ref: drawerPaperRef,
          onMouseEnter: handleDrawerMouseEnter,
          onMouseMove: handleDrawerMouseMove,
          onMouseLeave: handleDrawerMouseLeave,
        }}
      >
        {/* Logo removida do Drawer para evitar duplicidade com a AppBar */}
        <div className={classes.toolbarIcon} />
        {isMobile && (
          <Box 
            p={2} 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            style={{ 
              backgroundColor: theme.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)',
              margin: '10px',
              borderRadius: '12px'
            }}
          >
            <Typography variant="caption" style={{ opacity: 0.7, marginBottom: 4 }}>
              HORÁRIO DA EMPRESA
            </Typography>
            <Typography variant="h6" style={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
              <Clock timezone={timezone} />
            </Typography>
          </Box>
        )}
        <List className={classes.containerWithScroll} ref={sidebarListRef}>
          {mainListItems}
        </List>
        <Divider />
      </Drawer>

      <AppBar
        position="fixed"
        className={classes.appBar}
        color="primary"
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          {/* Esquerda: Logo em vez do botão de menu */}
          <img
            src={logoAppBar}
            alt="logo"
            className={classes.logoImg}
            style={{
              maxHeight: "30px",
              width: "auto",
              marginRight: "10px",
              cursor: "pointer"
            }}
            onClick={toggleDrawer}
          />

          {/* Título (desktop apenas) */}
          {!isMobile && (
            <Typography component="h2" variant="h6" color="inherit" noWrap className={classes.title}>
              {greaterThenSm && (user?.profile === "admin" || user?.profile === "super_admin") && user?.company?.dueDate ? (
                <>
                  {i18n.t("mainDrawer.appBar.user.message")} <b>{user.name}</b>,{" "}
                  {i18n.t("mainDrawer.appBar.user.messageEnd")} <b>{user.companyName || user?.company?.name}</b>! (
                  {i18n.t("mainDrawer.appBar.user.active")} {dateToClient(user?.company?.dueDate)})
                </>
              ) : (
                <>
                  {i18n.t("mainDrawer.appBar.user.message")} <b>{user.name}</b>,{" "}
                  {i18n.t("mainDrawer.appBar.user.messageEnd")} <b>{user.companyName || user?.company?.name}</b>!
                </>
              )}
            </Typography>
          )}

          {/* Direita: Ícones no scroller */}
          <div className={classes.topbarScroller}>
            {userToken === "enabled" && user?.company_id === 1 && !isMobile && (
              <Chip className={classes.chip} label={i18n.t("mainDrawer.appBar.user.token")} />
            )}

            {!isMobile && <VersionControl />}
            
            {localStorage.getItem("supportToken") && (
              <>
                <Chip
                  label={`SIMULANDO: ${user.companyName || user?.company?.name}`}
                  size="small"
                  style={{
                    backgroundColor: "#f44336",
                    color: "white",
                    fontWeight: "bold",
                    marginRight: 10,
                    padding: "0 8px",
                    height: 28,
                    fontSize: "0.75rem",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.2)"
                  }}
                />
                <Tooltip title="Sair da Simulação / Voltar para Suporte" arrow>
                  <IconButton
                    onClick={handleBackToSupport}
                    style={{ color: "white", backgroundColor: "rgba(255,0,0,0.2)", marginRight: 5, padding: 4 }}
                  >
                    <MeetingRoomIcon style={{ color: "white" }} />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {localStorage.getItem("originalToken") && (
              <>
                <Chip
                  label={`SIMULANDO FILIAL: ${user.companyName || user?.company?.name}`}
                  size="small"
                  style={{
                    backgroundColor: "#1D2B9F",
                    color: "white",
                    fontWeight: "bold",
                    marginRight: 10,
                    padding: "0 8px",
                    height: 28,
                    fontSize: "0.75rem",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.2)"
                  }}
                />
                <Tooltip title="Voltar para Empresa Master" arrow>
                  <IconButton
                    onClick={handleBackToMaster}
                    style={{ color: "white", backgroundColor: "rgba(29, 43, 159, 0.2)", marginRight: 5, padding: 4 }}
                  >
                    <MeetingRoomIcon style={{ color: "white" }} />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {!isMobile && (
              <Box
                display="flex"
                alignItems="center"
                style={{
                  background: "rgba(255, 255, 255, 0.08)",
                  padding: "4px 14px",
                  borderRadius: "12px",
                  marginRight: "8px",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  backdropFilter: "blur(4px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  color: "#fff"
                }}
              >
                <AccessTimeIcon style={{ fontSize: 18, marginRight: 8, opacity: 0.9 }} />
                <Typography
                  variant="subtitle2"
                  style={{
                    fontWeight: 600,
                    fontFamily: "'Inter', monospace",
                    letterSpacing: "0.5px",
                    fontSize: "0.85rem"
                  }}
                >
                  <Clock timezone={timezone} />
                </Typography>
              </Box>
            )}

            {!isMobile && (
              <Tooltip title="Alternar Tema" arrow>
                <IconButton edge="start" onClick={colorMode.toggleColorMode} style={{ padding: 4 }}>
                  {theme.mode === "dark" ? (
                    <Brightness7Icon style={{ color: "white" }} />
                  ) : (
                    <Brightness4Icon style={{ color: "white" }} />
                  )}
                </IconButton>
              </Tooltip>
            )}

            {!isMobile && (
              <Tooltip title="Atualizar Página" arrow>
                <IconButton
                  onClick={handleRefreshPage}
                  aria-label={i18n.t("mainDrawer.appBar.refresh")}
                  color="inherit"
                  style={{ color: "white", padding: 4 }}
                >
                  <CachedIcon style={{ color: "white" }} />
                </IconButton>
              </Tooltip>
            )}

            {!isMobile && user.id && <RemindersPopover />}
            {user.id && <NotificationsPopOver volume={volume} />}

            {!isMobile && <AnnouncementsPopover />}
            {!isMobile && <ChatPopover />}

            <StyledBadge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
              onClick={handleMenu}
            >
              <IconButton
                variant="contained"
                aria-describedby={anchorEl ? 'menu-appbar' : undefined}
                onClick={handleMenu}
                style={{ color: "white", padding: 4 }}
              >
                <Avatar alt="Multi100" className={classes.avatar2} src={profileUrl} />
              </IconButton>
            </StyledBadge>

            {/* Menu do usuário */}
            {userModalOpen && (
              <Suspense fallback={null}>
                <UserModal
                  open={userModalOpen}
                  onClose={() => setUserModalOpen(false)}
                  onImageUpdate={(newProfileUrl) => setProfileUrl(newProfileUrl)}
                  user_id={user?.id}
                />
              </Suspense>
            )}
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenSupport}>
                <WhatsAppIcon fontSize="small" style={{ marginRight: "10px", color: "#25D366" }} />
                {i18n.t("connections.callSupport")}
              </MenuItem>
              <MenuItem onClick={handleOpenUserModal}>{i18n.t("mainDrawer.appBar.user.profile")}</MenuItem>
              <MenuItem onClick={handleClickLogout}>{i18n.t("mainDrawer.appBar.user.logout")}</MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      <main className={clsx(classes.content, drawerOpen && classes.contentShift)}>
        <div className={classes.appBarSpacer} />
        <div 
          ref={contentWrapperRef}
          className={isMobile ? classes.contentWrapper : classes.contentWrapperNoBottom}
          style={isTicketsPage ? { overflowY: "hidden" } : { overflowY: "auto" }}
        >
          {children ? children : null}
        </div>
      </main>

      <Hidden smUp>
        {(
          // Corrigido 08/10/2026: header/rodapé não somem mais ao abrir uma
          // conversa — antes isso era escondido de propósito (isTicketView)
          // pra "dar mais espaço" ao chat, só que também tirava a navegação
          // da tela, deixando o usuário sem jeito de voltar sem usar o botão
          // nativo do navegador.
          <BottomNavigation
            value={bottomNavValue}
            onChange={(event, newValue) => {
              if (newValue === 4) {
                setDrawerOpen(!drawerOpen);
                return;
              }
              setBottomNavValue(newValue);
              const routes = ["/tickets", "/kanban", "/contacts", "/chats"];
              if (routes[newValue]) history.push(routes[newValue]);
            }}
            showLabels
            className={classes.bottomNav}
            style={{
              borderTop: theme.mode === 'light' ? '1px solid #e2e8f0' : '1px solid #2d3748',
              backgroundColor: theme.mode === 'light' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(15, 23, 42, 0.9)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
              height: '64px',
              position: 'fixed',
              bottom: 0,
              width: '100%',
              zIndex: 1300
            }}
          >
            <BottomNavigationAction
              label="Tickets"
              icon={
                <Badge badgeContent={notifications.filter(n => n.status !== "pending").length} color="secondary" overlap="rectangular">
                  <WhatsAppIcon />
                </Badge>
              }
            />
            <BottomNavigationAction
              label="Kanban"
              icon={<AccountTreeOutlinedIcon />}
            />
            <BottomNavigationAction
              label="Contatos"
              icon={<ContactPhoneOutlinedIcon />}
            />
            <BottomNavigationAction
              label="Chat"
              icon={<ChatIcon />}
            />
            <BottomNavigationAction
              label="Menu"
              icon={<MoreHorizIcon />}
            />
          </BottomNavigation>
        )}
      </Hidden>

      <SystemNoticeGate />
      <PWAModal
        open={pwaModalOpen}
        onClose={handleClosePwaModal}
        onInstall={handleInstallFromModal}
      />
      <AIAssistant />
    </div>
  );
};

export default LoggedInLayout;
